import { useAppSelector } from "../../../../../store/hooks";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import { useEffect, useState, useRef, useCallback } from "react";
import React from "react";
import { fetchApiGetUuid } from "../../../../../api/api";
import { EventAdditionalItemsComponent } from "../../../../_default/interfaces/base";
import "../EventsMenu/EventsMenu.scss";

const menuInputStyle: React.CSSProperties = {
  background: "#444",
  border: "0px solid #555",
  borderRadius: "3px",
  color: "#fff",
  height: "22px",
  paddingLeft: "5px",
  paddingRight: "5px",
  width: "100%",
  boxShadow:
    "0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)",
  outline: "none",
  fontSize: "12px",
};

interface Props {
  id: number;
  triggerResize: () => void;
}

interface EventGroup {
  id: number;
  hash: string;
  groupName: string;
  hasTickets: boolean;
  location: string;
}

interface GroupRow {
  rowId: string;
  group: EventGroup | null;
  searchQuery: string;
  urlParam: string;
  isSearching: boolean;
  loading: boolean;
  error: string | null;
  filteredGroups: EventGroup[];
  isMenuClicked: boolean;
}

function makeRow(): GroupRow {
  return {
    rowId: Math.random().toString(36).slice(2),
    group: null,
    searchQuery: "",
    urlParam: "",
    isSearching: false,
    loading: false,
    error: null,
    filteredGroups: [],
    isMenuClicked: false,
  };
}

export default function EventAdditionalItemsMenu({ id, triggerResize }: Props) {
  const component = useAppSelector(
    (state) => state.builder.structure.components[id]
  )! as EventAdditionalItemsComponent;

  const attrs = component.attributes;
  const showReadMore = attrs.show_read_more ?? false;
  const showReadMoreText = attrs.show_read_more_text ?? "";
  const noUrlParamShowText = attrs.no_url_param_show_text ?? false;
  const noUrlParamText = attrs.no_url_param_text ?? "";
  const noUrlParamShowButton = attrs.no_url_param_show_button ?? false;
  const noUrlParamButtonText = attrs.no_url_param_button_text ?? "";
  const noUrlParamButtonLink = attrs.no_url_param_button_link ?? "";

  const [rows, setRows] = useState<GroupRow[]>([makeRow()]);
  const searchTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  const searchRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Load saved groups on mount
  useEffect(() => {
    const attrs = component.attributes as any;
    const savedGroups = attrs.event_groups;
    if (Array.isArray(savedGroups) && savedGroups.length > 0) {
      setRows(
        savedGroups.map((g: any) => ({
          rowId: Math.random().toString(36).slice(2),
          group: g.groupHash
            ? {
                id: 0,
                hash: g.groupHash,
                groupName: g.groupName,
                hasTickets: false,
                location: "",
              }
            : null,
          searchQuery: g.groupName || "",
          urlParam: g.urlParam || "",
          isSearching: false,
          loading: false,
          error: null,
          filteredGroups: [],
          isMenuClicked: false,
        }))
      );
    }
    triggerResize();
  }, []);

  const saveToStore = useCallback(
    (updatedRows: GroupRow[]) => {
      const groups = updatedRows
        .filter((r) => r.group)
        .map((r) => ({
          groupHash: r.group!.hash,
          groupName: r.group!.groupName,
          urlParam: r.urlParam,
        }));
      updateStructureComponent(id, "attributes.event_groups", groups);
    },
    [id]
  );

  const patchRow = useCallback(
    (rowId: string, patch: Partial<GroupRow>, save = false) => {
      setRows((prev) => {
        const next = prev.map((r) =>
          r.rowId === rowId ? { ...r, ...patch } : r
        );
        if (save) saveToStore(next);
        return next;
      });
    },
    [saveToStore]
  );

  const searchEventGroups = useCallback(
    async (rowId: string, query: string) => {
      patchRow(rowId, { loading: true, error: null });
      try {
        const response = await fetchApiGetUuid<any>(
          `/events-groups?name=${encodeURIComponent(query)}`
        );
        let groups: EventGroup[] = [];
        if (Array.isArray(response)) {
          groups = response;
        } else if (response?.data && Array.isArray(response.data)) {
          groups = response.data;
        }
        patchRow(rowId, { filteredGroups: groups, loading: false });
      } catch {
        patchRow(rowId, {
          error: "Failed to load event groups",
          loading: false,
        });
      }
    },
    [patchRow]
  );

  const handleAddRow = () => {
    setRows((prev) => [...prev, makeRow()]);
    triggerResize();
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.rowId !== rowId);
      const result = next.length === 0 ? [makeRow()] : next;
      saveToStore(result);
      return result;
    });
    triggerResize();
  };

  const handleSearchChange = (rowId: string, value: string) => {
    patchRow(rowId, { searchQuery: value });
    if (searchTimeouts.current[rowId])
      clearTimeout(searchTimeouts.current[rowId]);
    searchTimeouts.current[rowId] = setTimeout(
      () => searchEventGroups(rowId, value),
      300
    );
  };

  const handleSearchFocus = (rowId: string) => {
    const row = rows.find((r) => r.rowId === rowId);
    patchRow(rowId, { isSearching: true });
    if (!row?.group) searchEventGroups(rowId, row?.searchQuery || "");
    triggerResize();
  };

  const handleSearchBlur = (rowId: string) => {
    const row = rows.find((r) => r.rowId === rowId);
    if (!row?.isMenuClicked) {
      setTimeout(() => {
        patchRow(rowId, { isSearching: false });
        triggerResize();
      }, 200);
    }
    patchRow(rowId, { isMenuClicked: false });
  };

  const handleSelectGroup = (rowId: string, group: EventGroup) => {
    setRows((prev) => {
      const next = prev.map((r) =>
        r.rowId === rowId
          ? { ...r, group, searchQuery: group.groupName, isSearching: false }
          : r
      );
      saveToStore(next);
      return next;
    });
    triggerResize();
  };

  const handleClearGroup = (e: React.MouseEvent, rowId: string) => {
    e.stopPropagation();
    setRows((prev) => {
      const next = prev.map((r) =>
        r.rowId === rowId ? { ...r, group: null, searchQuery: "" } : r
      );
      saveToStore(next);
      return next;
    });
  };

  const handleUrlParamChange = (rowId: string, value: string) => {
    setRows((prev) => {
      const next = prev.map((r) =>
        r.rowId === rowId ? { ...r, urlParam: value } : r
      );
      saveToStore(next);
      return next;
    });
  };

  const hideZeroPrice = attrs.hide_zero_price ?? false;

  const toggleHideZeroPrice = () => {
    updateStructureComponent(id, "attributes.hide_zero_price", !hideZeroPrice);
  };

  const patchAttr = (path: string, value: unknown) => {
    updateStructureComponent(id, `attributes.${path}`, value);
  };

  const renderToggleControl = (
    label: string,
    checked: boolean,
    onToggle: () => void
  ) => (
    <div
      style={{
        display: "flex",
        gap: "5px",
        alignItems: "center",
        flexShrink: 0,
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onToggle();
      }}
    >
      <label className="switch">
        <input type="checkbox" checked={checked} readOnly />
        <span className="slider round"></span>
      </label>
      <span style={{ fontSize: "12px", whiteSpace: "nowrap" }}>{label}</span>
    </div>
  );

  const renderToggleRow = (
    label: string,
    checked: boolean,
    onToggle: () => void
  ) => renderToggleControl(label, checked, onToggle);

  const toggleInputRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    minWidth: 0,
  };

  const inlineMenuInputStyle: React.CSSProperties = {
    ...menuInputStyle,
    flex: 1,
    minWidth: 0,
  };

  return (
    <div className="d-flex flex-column" style={{ gap: "6px" }}>
      <div
        className="d-flex flex-column"
        style={{
          gap: "8px",
          marginTop: "5px",
          paddingLeft: "7px",
          paddingRight: "7px",
        }}
      >
        {renderToggleRow(
          "Show price if 0.00",
          !hideZeroPrice,
          toggleHideZeroPrice
        )}

        {renderToggleRow("Show read more", showReadMore, () => {
          const next = !showReadMore;
          patchAttr("show_read_more", next);
          if (next && !showReadMoreText) {
            patchAttr("show_read_more_text", "Loe edasi");
          }
          triggerResize();
        })}
        {showReadMore && (
          <input
            type="text"
            value={showReadMoreText}
            onChange={(e) => patchAttr("show_read_more_text", e.target.value)}
            placeholder="Loe edasi"
            style={menuInputStyle}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}

        <span style={{ fontSize: "12px", color: "#ccc" }}>
          If no url parameter set then show:
        </span>

        <div style={toggleInputRowStyle}>
          {renderToggleControl("Text", noUrlParamShowText, () => {
            patchAttr("no_url_param_show_text", !noUrlParamShowText);
            triggerResize();
          })}
          {noUrlParamShowText && (
            <input
              type="text"
              value={noUrlParamText}
              onChange={(e) => patchAttr("no_url_param_text", e.target.value)}
              style={inlineMenuInputStyle}
              onMouseDown={(e) => e.stopPropagation()}
            />
          )}
        </div>

        <div style={toggleInputRowStyle}>
          {renderToggleControl("Button", noUrlParamShowButton, () => {
            patchAttr("no_url_param_show_button", !noUrlParamShowButton);
            triggerResize();
          })}
          {noUrlParamShowButton && (
            <input
              type="text"
              value={noUrlParamButtonText}
              onChange={(e) =>
                patchAttr("no_url_param_button_text", e.target.value)
              }
              style={inlineMenuInputStyle}
              onMouseDown={(e) => e.stopPropagation()}
            />
          )}
        </div>
        {noUrlParamShowButton && (
          <div style={toggleInputRowStyle}>
            <span
              style={{
                fontSize: "12px",
                color: "#ccc",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Link:
            </span>
            <input
              type="text"
              value={noUrlParamButtonLink}
              onChange={(e) =>
                patchAttr("no_url_param_button_link", e.target.value)
              }
              placeholder="/page or https://..."
              style={inlineMenuInputStyle}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>

      {rows.map((row) => (
        <div
          key={row.rowId}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            padding: "6px 8px",

            borderRadius: "4px",
          }}
        >
          {/* URL Param input */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "6px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  fontSize: "12px",
                  color: "#ccc",
                  whiteSpace: "nowrap",
                }}
              >
                URL Param:
              </span>
              <input
                className="b-mx-1 content-width-menu_input"
                type="text"
                value={row.urlParam}
                onChange={(e) =>
                  handleUrlParamChange(row.rowId, e.target.value)
                }
                placeholder="e.g. depart"
                style={{
                  border: "1px solid transparent",
                  borderRadius: "3px",
                  color: "#fff",
                  padding: "2px 6px",
                  fontSize: "12px",
                  width: "110px",
                  outline: "none",
                }}
              />
            </div>
            <button
              onClick={() => handleRemoveRow(row.rowId)}
              title="Remove group"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#e60000",
                padding: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <DeleteIcon style={{ fontSize: "15px" }} />
            </button>
          </div>

          {/* Group search */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{ fontSize: "12px", color: "#ccc", whiteSpace: "nowrap" }}
            >
              Group:
            </span>
            <div
              className="b-mx-1 content-width-menu_input position-relative"
              style={{
                minWidth: 0,
                padding: "5px 5px",
                height: "22px",
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{
                  borderRadius: "4px",
                  padding: "2px 4px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  patchRow(row.rowId, { isMenuClicked: true });
                  searchRefs.current[row.rowId]?.focus();
                }}
              >
                <input
                  ref={(el) => {
                    searchRefs.current[row.rowId] = el;
                  }}
                  type="text"
                  value={row.searchQuery}
                  onChange={(e) =>
                    handleSearchChange(row.rowId, e.target.value)
                  }
                  onFocus={() => handleSearchFocus(row.rowId)}
                  onBlur={() => handleSearchBlur(row.rowId)}
                  placeholder={row.group ? "" : "Search groups..."}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    width: "100%",
                    outline: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "12px",
                  }}
                />
                {row.group ? (
                  <ClearIcon
                    style={{
                      fontSize: "15px",
                      marginLeft: "4px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                    onClick={(e) => handleClearGroup(e, row.rowId)}
                  />
                ) : (
                  <SearchIcon
                    style={{
                      fontSize: "15px",
                      marginLeft: "4px",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>

              {row.isSearching && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    maxHeight: "200px",
                    overflowY: "auto",
                    top: "24px",
                    background: "#323232",
                    borderRadius: "2px",
                    zIndex: 1000,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    patchRow(row.rowId, { isMenuClicked: true });
                  }}
                >
                  {row.loading ? (
                    <div className="dropdown-item builder-dropdown__item">
                      Loading...
                    </div>
                  ) : row.error ? (
                    <div className="dropdown-item builder-dropdown__item">
                      {row.error}
                    </div>
                  ) : row.filteredGroups.length > 0 ? (
                    row.filteredGroups.map((g) => (
                      <div
                        key={g.hash}
                        className={
                          "dropdown-item builder-dropdown__item" +
                          (row.group?.hash === g.hash
                            ? " builder-dropdown__item--active"
                            : "")
                        }
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectGroup(row.rowId, g);
                        }}
                      >
                        {g.groupName}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item builder-dropdown__item">
                      No event groups found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={handleAddRow}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          background: "transparent",
          border: "1px dashed #555",
          borderRadius: "4px",
          color: "#aaa",
          cursor: "pointer",
          padding: "4px 8px",
          fontSize: "12px",
        }}
      >
        <AddIcon style={{ fontSize: "15px" }} />
        Add Group
      </button>
    </div>
  );
}
